import React from 'react';
import { useI18n } from '../../context/I18nContext';
import { Icons } from '../icons/Icons';
import ColorSelector from './ColorSelector';
import TerminalSelector from './TerminalSelector';
import SectionSelector from './SectionSelector';
import SheathSelector from './SheathSelector';

const StandardCableForm = ({
  currentCable,
  handleInputChange,
  settings,
  terminali,
  addCable,
  saveAsNewCable,
  cancelEdit,
  editingIndex,
  keepAllSettings,
  setKeepAllSettings,
  addMorsettoDirection,
  swapAB,
  availableSheaths,
  onAddSheath,
  onDeleteSheath
}) => {
  const { t } = useI18n();
  
  return (
    <div className="flex flex-col h-full">
      {/* Contenuto principale con scroll se necessario */}
      <div className="flex-1 space-y-6 overflow-y-auto pr-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('cableForm.siglatura')}
              {settings.marcaturePerCollegamento ? (
                <span className="text-xs text-gray-400 ml-1">{t('cableForm.disabledConnectionMode')}</span>
              ) : (
                <span className="text-xs text-gray-500 ml-1">{t('cableForm.optional')}</span>
              )}
            </label>
            <input
              type="text"
              value={settings.marcaturePerCollegamento ? '' : currentCable.siglatura}
              onChange={(e) => !settings.marcaturePerCollegamento && handleInputChange('siglatura', e.target.value)}
              disabled={settings.marcaturePerCollegamento}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 text-base ${settings.marcaturePerCollegamento
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : ''
              }`}
              placeholder={settings.marcaturePerCollegamento
                ? t('cableForm.disabledUseConnectionMode')
                : t('cableForm.placeholderSignature')
              }
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">
              {t('cableForm.group')}
              <span className="text-xs text-gray-500 ml-1">{t('cableForm.optional')}</span>
            </label>
            <input
              type="text"
              value={currentCable.gruppo}
              onChange={(e) => handleInputChange('gruppo', e.target.value)}
              className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 text-base"
              placeholder={t('cableForm.placeholderGroup')}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('cableForm.color')}</label>
            <ColorSelector
              value={currentCable.colore}
              onChange={(color) => handleInputChange('colore', color)}
              language={settings.lingua}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('cableForm.section')}</label>
            <SectionSelector
              value={currentCable.sezione}
              onChange={(sezione) => handleInputChange('sezione', sezione)}
              useAWG={settings.useAWG}
              currentColor={currentCable.colore}
              className="w-full"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">{t('cableForm.lengthMm')}</label>
          <input
            type="number"
            value={currentCable.lunghezza}
            onChange={(e) => handleInputChange('lunghezza', parseInt(e.target.value))}
            className="w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('cableForm.terminalA')}</label>
            <TerminalSelector
              value={currentCable.terminaleA}
              onChange={(terminal) => handleInputChange('terminaleA', terminal)}
              terminali={terminali}
              settings={settings}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('cableForm.terminalB')}</label>
            <TerminalSelector
              value={currentCable.terminaleB}
              onChange={(terminal) => handleInputChange('terminaleB', terminal)}
              terminali={terminali}
              settings={settings}
              className="w-full"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Campo A */}
          <div className="flex-1 relative">
            <label className="block text-sm font-medium mb-1">{t('cableForm.connectionA')}</label>
            <input
              type="text"
              value={currentCable.collegamentoA}
              onChange={(e) => handleInputChange('collegamentoA', e.target.value)}
              className="w-full px-3 py-2 pr-12 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={t('cableForm.placeholderConnectionA')}
            />

            {/* Frecce UP/DOWN sempre visibili per collegamentoA */}
            <div className="absolute right-1 top-7 bottom-1 flex flex-col">
              <button
                type="button"
                onClick={() => addMorsettoDirection('collegamentoA', 'up')}
                className="flex-1 px-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-t text-xs font-bold transition-colors flex items-center justify-center"
                title={t('cableForm.addArrowUp')}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => addMorsettoDirection('collegamentoA', 'down')}
                className="flex-1 px-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-b text-xs font-bold transition-colors flex items-center justify-center"
                title={t('cableForm.addArrowDown')}
              >
                ▼
              </button>
            </div>
          </div>

          {/* 🔄 PULSANTE SWAP al centro */}
          <div className="flex-shrink-0 pt-6">
            <button
              type="button"
              onClick={() => swapAB()}
              className="bg-white border-2 border-blue-300 hover:border-blue-500 text-blue-600 hover:text-blue-800 p-2 rounded-full shadow-sm hover:shadow-md transition-all duration-200 group"
              title={t('cableForm.swapTerminals')}
            >
              <Icons.Swap className="w-4 h-4 group-hover:scale-110 transition-transform" />
            </button>
          </div>

          {/* Campo B */}
          <div className="flex-1 relative">
            <label className="block text-sm font-medium mb-1">{t('cableForm.connectionB')}</label>
            <input
              type="text"
              value={currentCable.collegamentoB}
              onChange={(e) => handleInputChange('collegamentoB', e.target.value)}
              className="w-full px-3 py-2 pr-12 border rounded-md focus:ring-2 focus:ring-blue-500"
              placeholder={t('cableForm.placeholderConnectionB')}
            />

            {/* Frecce UP/DOWN sempre visibili per collegamentoB */}
            <div className="absolute right-1 top-7 bottom-1 flex flex-col">
              <button
                type="button"
                onClick={() => addMorsettoDirection('collegamentoB', 'up')}
                className="flex-1 px-2 bg-green-100 hover:bg-green-200 text-green-700 rounded-t text-xs font-bold transition-colors flex items-center justify-center"
                title={t('cableForm.addArrowUp')}
              >
                ▲
              </button>
              <button
                type="button"
                onClick={() => addMorsettoDirection('collegamentoB', 'down')}
                className="flex-1 px-2 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-b text-xs font-bold transition-colors flex items-center justify-center"
                title={t('cableForm.addArrowDown')}
              >
                ▼
              </button>
            </div>
          </div>
        </div>

        {/* Campo Guaina - spostato in fondo */}
        <div>
          <label className="block text-sm font-medium mb-1">
            {t('cableForm.sheath')}
            <span className="text-xs text-gray-500 ml-1">{t('cableForm.optional')}</span>
          </label>
          <SheathSelector
            value={currentCable.guaina}
            onChange={(guainaId) => handleInputChange('guaina', guainaId)}
            availableSheaths={availableSheaths || []}
            onAddSheath={onAddSheath}
            onDeleteSheath={onDeleteSheath}
            className="w-full"
          />
        </div>
      </div>

      {/* Pulsanti azioni */}
      <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col md:flex-row md:justify-end gap-3">
        <button
          onClick={addCable}
          className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
        >
          {editingIndex >= 0 ? (
            <>
              <Icons.Save className="w-4 h-4" />
              {t('cableForm.saveChanges')}
            </>
          ) : (
            <>
              <Icons.Plus className="w-4 h-4" />
              {t('cableForm.addCable')}
            </>
          )}
        </button>

        {editingIndex >= 0 && (
          <button
            onClick={saveAsNewCable}
            className="flex-1 md:flex-none bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
          >
            <Icons.Plus className="w-4 h-4" />
            {t('cableForm.saveAsNew')}
          </button>
        )}

        {editingIndex >= 0 && (
          <button
            onClick={cancelEdit}
            className="flex-1 md:flex-none px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <Icons.X className="w-4 h-4" />
            {t('common.cancel')}
          </button>
        )}

        {editingIndex < 0 && (
          <div className="flex items-center gap-2 pt-3 border-t border-gray-200 mt-3 md:mt-0 md:border-t-0">
            <input
              type="checkbox"
              id="keepAllSettings"
              checked={keepAllSettings}
              onChange={(e) => setKeepAllSettings(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="keepAllSettings" className="text-sm text-gray-600 cursor-pointer">
              {t('cableForm.keepAllSettings')}
            </label>
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardCableForm;